// @ts-nocheck
import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { supabase } from '../../supabaseClient';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '../../types';
import type { AdminStats } from '../../types/admin';
import { AdminStats as AdminStatsComponent } from './AdminStats';
import { AdminActivityFeed } from './AdminActivityFeed';
import { AdminHealthDashboard } from './AdminHealthDashboard';
import { AdminSidebarSearch } from './AdminSidebarSearch';
// Lazy load heavy components
const AdminRevenueDashboard = lazy(() => import('./AdminRevenueDashboard'));
const AdminAnalyticsEnhanced = lazy(() => import('./AdminAnalyticsEnhanced'));
const AdminUsersEnhanced = lazy(() => import('./AdminUsersEnhanced'));
const AdminCriticalAlerts = lazy(() => import('./AdminCriticalAlerts'));
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, subDays } from 'date-fns';

interface AdminDashboardProps {
  session: Session;
  profile: Profile | null;
  onNavigate?: (page: string) => void;
}

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe'];

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ session, profile, onNavigate }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'revenue' | 'analytics' | 'users' | 'alerts'>('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalTopics: 0,
    totalSubscriptions: 0,
    totalNotifications: 0,
    activeSubscriptions: 0,
  });
  const [userGrowth, setUserGrowth] = useState<Array<{ date: string; users: number }>>([]);
  const [subscriptionDistribution, setSubscriptionDistribution] = useState<
    Array<{ name: string; value: number }>
  >([]);

  // Define async functions first
  const loadStatistics = async () => {
    setLoading(true);
    try {
      const [usersRes, topicsRes, subsRes, notifsRes, activeSubsRes] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('law_topics').select('id', { count: 'exact', head: true }),
        supabase
          .from('user_topic_subscriptions')
          .select('id', { count: 'exact', head: true }),
        supabase.from('notifications').select('id', { count: 'exact', head: true }),
        supabase
          .from('user_topic_subscriptions')
          .select('id', { count: 'exact', head: true })
          .eq('email_enabled', true),
      ]);

      setStats({
        totalUsers: usersRes.count || 0,
        totalTopics: topicsRes.count || 0,
        totalSubscriptions: subsRes.count || 0,
        totalNotifications: notifsRes.count || 0,
        activeSubscriptions: activeSubsRes.count || 0,
      });
    } catch (err) {
      console.error('Error loading statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadChartData = async () => {
    try {
      // User growth over last 30 days
      const { data: users } = await supabase
        .from('profiles')
        .select('updated_at')
        .order('updated_at', { ascending: true });

      if (users) {
        const last30Days = Array.from({ length: 30 }, (_, i) => {
          const date = subDays(new Date(), 29 - i);
          return format(date, 'MMM dd');
        });

        const userCounts = last30Days.map((dateStr) => {
          const date = subDays(new Date(), 29 - last30Days.indexOf(dateStr));
          const count = users.filter((u) => {
            const userDate = new Date(u.updated_at);
            return userDate <= date;
          }).length;
          return { date: dateStr, users: count };
        });

        setUserGrowth(userCounts);
      }

      // Subscription distribution by topic
      const { data: subscriptions } = await supabase
        .from('user_topic_subscriptions')
        .select('law_topics:topic_id(name)');

      if (subscriptions) {
        const topicCounts: Record<string, number> = {};
        subscriptions.forEach((sub: any) => {
          const topicName = sub.law_topics?.name || 'Unknown';
          topicCounts[topicName] = (topicCounts[topicName] || 0) + 1;
        });

        setSubscriptionDistribution(
          Object.entries(topicCounts).map(([name, value]) => ({ name, value }))
        );
      }
    } catch (err) {
      console.error('Error loading chart data:', err);
    }
  };

  // Debounce helper
  const debounce = (func: () => Promise<void>, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(), delay);
    };
  };

  // Create memoized debounced functions
  const debouncedLoadStatistics = useMemo(
    () => debounce(loadStatistics, 5000),
    []
  );

  const debouncedLoadChartData = useMemo(
    () => debounce(loadChartData, 5000),
    []
  );

  useEffect(() => {
    loadStatistics();
    loadChartData();

    // Set up real-time subscriptions
    const profilesChannel = supabase
      .channel('admin-profiles-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
        },
        () => {
          debouncedLoadStatistics();
        }
      )
      .subscribe();

    const topicsChannel = supabase
      .channel('admin-topics-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'law_topics',
        },
        () => {
          debouncedLoadStatistics();
          debouncedLoadChartData();
        }
      )
      .subscribe();

    const subscriptionsChannel = supabase
      .channel('admin-subscriptions-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_topic_subscriptions',
        },
        () => {
          debouncedLoadStatistics();
          debouncedLoadChartData();
        }
      )
      .subscribe();

    const notificationsChannel = supabase
      .channel('admin-notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
        },
        () => {
          debouncedLoadStatistics();
        }
      )
      .subscribe();

    return () => {
      profilesChannel.unsubscribe();
      topicsChannel.unsubscribe();
      subscriptionsChannel.unsubscribe();
      notificationsChannel.unsubscribe();
    };
  }, [debouncedLoadStatistics, debouncedLoadChartData]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Tabs Navigation */}
      <div className="flex flex-wrap gap-2 border-b border-luna-secondary/30 pb-4">
        {[
          { id: 'overview', label: '📊 Overview' },
          { id: 'revenue', label: '💰 Revenue' },
          { id: 'analytics', label: '📈 Analytics' },
          { id: 'users', label: '👥 Users' },
          { id: 'alerts', label: '⚠️ Alerts' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-luna-secondary text-white'
                : 'text-luna-text-light/70 dark:text-luna-text-dark/70 hover:text-luna-secondary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4 sm:mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
                Admin Dashboard
              </h1>
              <p className="text-sm sm:text-base text-luna-text-light/70 dark:text-luna-text-dark/70">
                Overview of your LRDEnE application
              </p>
            </div>
            <button
              onClick={() => {
                loadStatistics();
                loadChartData();
              }}
              disabled={loading}
              className="w-full sm:w-auto px-4 py-2.5 sm:py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark hover:bg-luna-secondary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[44px] sm:min-h-[40px] touch-manipulation"
              title="Refresh data"
            >
              <span className={`material-symbols-outlined ${loading ? 'animate-spin' : ''}`}>
                refresh
              </span>
              <span>Refresh</span>
            </button>
          </div>

          {/* System Health Status */}
          <AdminHealthDashboard />

          {/* Statistics Cards */}
          <AdminStatsComponent stats={stats} loading={loading} />

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* User Growth Chart */}
            <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-luna-text-light dark:text-luna-text-dark">
                User Growth (Last 30 Days)
              </h3>
              <div className="h-[250px] sm:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={userGrowth} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#6b7280" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis 
                      stroke="#6b7280" 
                      tick={{ fontSize: 12 }}
                      width={40}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line
                      type="monotone"
                      dataKey="users"
                      stroke="#8884d8"
                      strokeWidth={2}
                      dot={{ fill: '#8884d8', r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Subscription Distribution */}
            <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-luna-text-light dark:text-luna-text-dark">
                Subscription Distribution
              </h3>
              <div className="h-[250px] sm:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={subscriptionDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {subscriptionDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{
                        fontSize: '12px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Activity Feed & Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Activity Feed */}
            <div className="lg:col-span-1 bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6">
              <AdminActivityFeed />
            </div>

            {/* Quick Actions */}
            <div className="lg:col-span-2 bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-luna-text-light dark:text-luna-text-dark">
                Quick Actions
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <button 
                  onClick={() => onNavigate?.('topics')}
                  className="p-4 sm:p-5 border-2 border-luna-secondary/30 rounded-lg hover:bg-luna-secondary/10 active:bg-luna-secondary/20 transition-colors text-left min-h-[100px] sm:min-h-[120px] touch-manipulation"
                >
                  <span className="material-symbols-outlined text-2xl sm:text-3xl text-luna-secondary mb-2 block">add</span>
                  <div className="font-medium text-sm sm:text-base text-luna-text-light dark:text-luna-text-dark mb-1">
                    Create New Topic
                  </div>
                  <div className="text-xs sm:text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                    Add a new law topic
                  </div>
                </button>
                <button 
                  onClick={() => onNavigate?.('news')}
                  className="p-4 sm:p-5 border-2 border-luna-secondary/30 rounded-lg hover:bg-luna-secondary/10 active:bg-luna-secondary/20 transition-colors text-left min-h-[100px] sm:min-h-[120px] touch-manipulation"
                >
                  <span className="material-symbols-outlined text-2xl sm:text-3xl text-luna-secondary mb-2 block">article</span>
                  <div className="font-medium text-sm sm:text-base text-luna-text-light dark:text-luna-text-dark mb-1">
                    Create News Update
                  </div>
                  <div className="text-xs sm:text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                    Announce new features
                  </div>
                </button>
                <button 
                  onClick={() => onNavigate?.('settings')}
                  className="p-4 sm:p-5 border-2 border-luna-secondary/30 rounded-lg hover:bg-luna-secondary/10 active:bg-luna-secondary/20 transition-colors text-left min-h-[100px] sm:min-h-[120px] touch-manipulation sm:col-span-2 lg:col-span-1"
                >
                  <span className="material-symbols-outlined text-2xl sm:text-3xl text-luna-secondary mb-2 block">settings</span>
                  <div className="font-medium text-sm sm:text-base text-luna-text-light dark:text-luna-text-dark mb-1">
                    System Settings
                  </div>
                  <div className="text-xs sm:text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                    Configure system
                  </div>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'revenue' && (
        <Suspense fallback={
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luna-secondary"></div>
          </div>
        }>
          <AdminRevenueDashboard />
        </Suspense>
      )}

      {activeTab === 'analytics' && (
        <Suspense fallback={
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luna-secondary"></div>
          </div>
        }>
          <AdminAnalyticsEnhanced />
        </Suspense>
      )}

      {activeTab === 'users' && (
        <Suspense fallback={
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luna-secondary"></div>
          </div>
        }>
          <AdminUsersEnhanced />
        </Suspense>
      )}

      {activeTab === 'alerts' && (
        <Suspense fallback={
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luna-secondary"></div>
          </div>
        }>
          <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-secondary/30 p-6">
            <AdminCriticalAlerts />
          </div>
        </Suspense>
      )}
    </div>
  );
};

export default AdminDashboard;
